/**
 * Data preparation for the scatter and bubble chart.
 *
 * Distinct from the cartesian normalizer next door: there, rows are categories
 * on a shared axis and a measure may be missing without costing the row its
 * place. Here both axes are measures, so a point with a non-numeric x or y has
 * nowhere to be drawn at all and must leave the chart. Groups are the colour
 * dimension rather than the columns, and the optional third measure drives mark
 * area rather than a second line.
 */

import { isValidColor } from "../chartUtils";

/** One plotted mark. Positions are resolved numbers; r is in pixels. */
export interface ScatterPoint {
  /**
   * Position in the full point list, assigned at normalization.
   *
   * Carried on the datum rather than looked up later: recharts hands event
   * handlers a merged copy of the point, so reference equality against the
   * original array fails and an index search returns -1.
   */
  index: number;
  x: number;
  y: number;
  /** Raw third measure. Undefined when the chart is a plain scatter. */
  z?: number;
  /** Mark radius in px, already area-scaled. */
  r: number;
  label: string;
  groupId: string;
  groupLabel: string;
  color: string;
  /** The caller's original row, handed back to onPointClick. */
  source?: unknown;
}

export interface ScatterGroup {
  id: string;
  label: string;
  color: string;
  points: ScatterPoint[];
}

export interface NormalizedScatterData {
  groups: ScatterGroup[];
  /** Every point across every group, in input order. */
  points: ScatterPoint[];
  xDomain: [number, number];
  yDomain: [number, number];
  /** Round tick values inside each domain, supplied to the axes explicitly. */
  xTicks: number[];
  yTicks: number[];
  /** Observed range of the size measure, or null when there is none. */
  zRange: [number, number] | null;
  /** Rows dropped as unplottable. */
  skipped: number;
  warnings: string[];
}

/** Caller-supplied group config. A bare string is shorthand for the group id. */
export type GroupInput = string | { id: string; label?: string; color?: string };

/** Silhouette a mark is drawn as. */
export type MarkShape = "circle" | "diamond";

/**
 * Half-diagonal per unit radius that keeps a diamond's area equal to the circle it replaces.
 *
 * A circle of radius r covers πr²; a square rotated 45° with half-diagonal d covers 2d². Reusing
 * r directly as the half-diagonal would draw every mark at 2/π — about 64% — of its former area
 * while the size key went on claiming the old scale, quietly shrinking the chart's third measure
 * without saying so. √(π/2) is what lets the two shapes swap without restating what size is worth.
 */
export const DIAMOND_RATIO = Math.sqrt(Math.PI / 2);

/** How far a mark of circle-equivalent radius {@link r} actually reaches from its centre. */
export function markExtent(r: number, shape: MarkShape): number {
  return shape === "diamond" ? r * DIAMOND_RATIO : r;
}

/** Points for a diamond centred on (cx, cy) with the given half-diagonal. */
export function diamondPoints(cx: number, cy: number, extent: number): string {
  return `${cx},${cy - extent} ${cx + extent},${cy} ${cx},${cy + extent} ${cx - extent},${cy}`;
}

export interface NormalizeScatterOptions {
  xKey: string;
  yKey: string;
  /** Size measure. Ignored unless the chart is in bubble mode. */
  zKey?: string;
  groupKey?: string;
  labelKey?: string;
  groups?: GroupInput[];
  palette: readonly string[];
  otherColor: string;
  otherLabel: string;
  /** All-pairs colour cap. Groups past it fold into the neutral bucket. */
  maxGroups: number;
  /** Smallest and largest mark radius in px. */
  radiusRange: [number, number];
  /** Plain dot radius when no size measure is in play. */
  baseRadius: number;
  xStartsAtZero: boolean;
  yStartsAtZero: boolean;
}

function coerce(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() === "") return NaN;
  return Number(value);
}

function humanize(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Rounds a span to a step a reader can do arithmetic on: 1, 2, 2.5, or 5 times
 * a power of ten. Aiming for about five intervals keeps ticks legible.
 */
function niceStep(span: number): number {
  if (!(span > 0)) return 1;
  const rough = span / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const n = rough / magnitude;
  const factor = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return magnitude * factor;
}

/**
 * Frames an axis around its data with a little breathing room.
 *
 * The collapsed range is the case that matters: when every point shares one x
 * (or one y), min equals max and an unpadded domain hands recharts a zero-width
 * scale, which stacks every mark on the axis line or drops them entirely.
 *
 * The bounds are deliberately NOT snapped outward to a round number. Snapping
 * reads well on paper but inflates the plot whenever the round step is large
 * relative to the data: a backlog spanning 4 to 92 days snaps to an axis of -50
 * to 100, which wastes half the plot and implies negative ages that do not
 * exist. Round numbers belong on the ticks, which is what niceTicks is for.
 */
export function padDomain(
  min: number,
  max: number,
  startAtZero: boolean,
): [number, number] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];

  let lo: number;
  let hi: number;

  if (min === max) {
    // One distinct value: centre it in a band rather than pinning it to an edge.
    const pad = Math.abs(min) * 0.1 || 1;
    lo = min - pad;
    hi = max + pad;
  } else {
    const pad = (max - min) * 0.08;
    lo = min - pad;
    hi = max + pad;
  }

  if (startAtZero) lo = Math.min(0, lo);
  return [lo, hi];
}

/**
 * Round tick values falling inside a domain.
 *
 * Supplied explicitly because recharts, given a domain and a tick count, splits
 * the range evenly and happily labels an axis 4.65, 4.9, 5.15 - arithmetic no
 * reader wants to do. Ticks land on multiples of the step instead, and the
 * domain keeps its honest bounds.
 */
export function niceTicks(lo: number, hi: number): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || !(hi > lo)) return [];

  const step = niceStep(hi - lo);
  // Enough precision for the step without floating point trails like
  // 0.30000000000000004 reaching a label.
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);

  const ticks: number[] = [];
  const first = Math.ceil(lo / step) * step;
  // The epsilon keeps a tick that lands exactly on the upper bound.
  for (let value = first; value <= hi + step * 1e-9; value += step) {
    ticks.push(Number(value.toFixed(decimals)));
  }
  return ticks;
}

/**
 * Maps a size measure onto a mark radius by AREA, not by radius.
 *
 * This is the correctness point of a bubble chart. Readers compare the amount
 * of ink they see, so a value twice as large must cover twice the area, which
 * means the radius grows with the square root. Feeding the value straight to
 * the radius squares the difference and makes big values look four times
 * bigger than they are - the single most common way a bubble chart lies.
 *
 * The result is clamped into radiusRange so the smallest mark stays visible and
 * hoverable, and the largest cannot swallow its neighbours.
 */
export function radiusForValue(
  value: number,
  zMin: number,
  zMax: number,
  [rMin, rMax]: [number, number],
): number {
  if (!Number.isFinite(value)) return rMin;
  if (!(zMax > zMin)) return (rMin + rMax) / 2;
  const normalized = Math.min(1, Math.max(0, (value - zMin) / (zMax - zMin)));
  return rMin + Math.sqrt(normalized) * (rMax - rMin);
}

/**
 * Turn arbitrary caller rows into groups of positioned marks.
 *
 * Rows without a usable x and y are dropped and counted, because unlike a
 * category chart there is no axis slot to hold their place. Everything else is
 * kept: negatives, zeroes, and duplicate coordinates are all legitimate here.
 */
export function normalizeScatterData(
  data: unknown,
  options: NormalizeScatterOptions,
): NormalizedScatterData {
  const {
    xKey,
    yKey,
    zKey,
    groupKey,
    labelKey,
    groups: groupInput,
    palette,
    otherColor,
    otherLabel,
    maxGroups,
    radiusRange,
    baseRadius,
    xStartsAtZero,
    yStartsAtZero,
  } = options;

  const warnings: string[] = [];
  const empty: NormalizedScatterData = {
    groups: [],
    points: [],
    xDomain: [0, 1],
    yDomain: [0, 1],
    xTicks: [],
    yTicks: [],
    zRange: null,
    skipped: 0,
    warnings,
  };

  if (data == null) return empty;
  if (!Array.isArray(data)) {
    return { ...empty, warnings: ["Chart data must be an array."] };
  }

  // Pre-declared groups fix both order and colour, so a group keeps its hue
  // even when the current data happens not to contain it.
  const declared = new Map<string, { label?: string; color?: string }>();
  groupInput?.forEach((entry) => {
    const config = typeof entry === "string" ? { id: entry } : entry;
    if (config?.id) declared.set(config.id, { label: config.label, color: config.color });
  });

  let skipped = 0;
  let badPosition = 0;
  let badSize = 0;
  let negativeSize = 0;

  interface Raw {
    x: number;
    y: number;
    z?: number;
    label: string;
    groupId: string;
    source: unknown;
  }

  const raws: Raw[] = [];

  data.forEach((row, index) => {
    if (row == null || typeof row !== "object") {
      skipped += 1;
      return;
    }
    const record = row as Record<string, unknown>;

    const x = coerce(record?.[xKey]);
    const y = coerce(record?.[yKey]);
    // Both coordinates are required: a mark with only one has no position.
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      skipped += 1;
      badPosition += 1;
      return;
    }

    let z: number | undefined;
    if (zKey) {
      const rawZ = record?.[zKey];
      const parsed = coerce(rawZ);
      if (!Number.isFinite(parsed)) {
        badSize += 1;
      } else if (parsed < 0) {
        // Area cannot be negative, so there is no honest mark to draw.
        negativeSize += 1;
      } else {
        z = parsed;
      }
    }

    const rawLabel = labelKey ? record?.[labelKey] : undefined;
    const label =
      typeof rawLabel === "string" || typeof rawLabel === "number"
        ? String(rawLabel).trim()
        : "";

    const rawGroup = groupKey ? record?.[groupKey] : undefined;
    const groupId =
      typeof rawGroup === "string" || typeof rawGroup === "number"
        ? String(rawGroup).trim() || "Ungrouped"
        : groupKey
          ? "Ungrouped"
          : "__all__";

    raws.push({ x, y, z, label: label || `Point ${index + 1}`, groupId, source: row });
  });

  if (badPosition > 0) {
    warnings.push(
      `${badPosition} ${badPosition === 1 ? "point was" : "points were"} skipped because ${xKey} or ${yKey} was missing or not a number.`,
    );
  }
  if (skipped - badPosition > 0) {
    const n = skipped - badPosition;
    warnings.push(
      `${n} ${n === 1 ? "row was" : "rows were"} skipped because they are not objects.`,
    );
  }
  if (badSize > 0) {
    warnings.push(
      `${badSize} ${badSize === 1 ? "point has" : "points have"} no usable ${zKey} value and ${badSize === 1 ? "is" : "are"} drawn at the smallest size.`,
    );
  }
  if (negativeSize > 0) {
    warnings.push(
      `${negativeSize} negative ${zKey} ${negativeSize === 1 ? "value was" : "values were"} ignored. Mark area cannot represent a negative number.`,
    );
  }

  if (!raws.length) return { ...empty, skipped, warnings };

  // Size scale spans the whole chart, not each group, or marks would only be
  // comparable against their own colour.
  let zRange: [number, number] | null = null;
  if (zKey) {
    const sizes = raws.map((r) => r.z).filter((z): z is number => typeof z === "number");
    if (sizes.length) zRange = [Math.min(...sizes), Math.max(...sizes)];
  }

  // Order groups by first appearance unless the caller declared an order.
  const order: string[] = [];
  if (declared.size) {
    declared.forEach((_, id) => order.push(id));
  }
  raws.forEach((raw) => {
    if (!order.includes(raw.groupId)) order.push(raw.groupId);
  });

  const cap = Math.max(1, Math.floor(maxGroups));
  const kept = order.slice(0, cap);
  const folded = order.slice(cap);
  if (folded.length) {
    warnings.push(
      `${folded.length} ${folded.length === 1 ? "group was" : "groups were"} folded into "${otherLabel}". Scattered marks can sit against any other mark, so only ${cap} hues stay reliably distinct.`,
    );
  }

  const colorFor = (groupId: string, index: number) => {
    const custom = declared.get(groupId)?.color;
    if (isValidColor(custom)) return custom;
    return palette[index % palette.length] ?? otherColor;
  };
  const labelFor = (groupId: string) => {
    const custom = declared.get(groupId)?.label?.trim();
    if (custom) return custom;
    return groupId === "__all__" ? "" : humanize(groupId);
  };

  const groupMeta = new Map<string, { id: string; label: string; color: string }>();
  kept.forEach((groupId, i) => {
    groupMeta.set(groupId, {
      id: groupId,
      label: labelFor(groupId),
      color: colorFor(groupId, i),
    });
  });
  folded.forEach((groupId) => {
    groupMeta.set(groupId, { id: "__other__", label: otherLabel, color: otherColor });
  });

  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  const points: ScatterPoint[] = raws.map((raw, index) => {
    const meta = groupMeta.get(raw.groupId)!;
    if (raw.x < xMin) xMin = raw.x;
    if (raw.x > xMax) xMax = raw.x;
    if (raw.y < yMin) yMin = raw.y;
    if (raw.y > yMax) yMax = raw.y;

    const r =
      zRange && typeof raw.z === "number"
        ? radiusForValue(raw.z, zRange[0], zRange[1], radiusRange)
        : zRange
          ? // Size measure exists but this point lacks one: smallest mark.
            radiusRange[0]
          : baseRadius;

    return {
      index,
      x: raw.x,
      y: raw.y,
      z: raw.z,
      r,
      label: raw.label,
      groupId: meta.id,
      groupLabel: meta.label,
      color: meta.color,
      source: raw.source,
    };
  });

  const groups: ScatterGroup[] = [];
  points.forEach((point) => {
    let group = groups.find((g) => g.id === point.groupId);
    if (!group) {
      group = { id: point.groupId, label: point.groupLabel, color: point.color, points: [] };
      groups.push(group);
    }
    group.points.push(point);
  });

  const xDomain = padDomain(xMin, xMax, xStartsAtZero);
  const yDomain = padDomain(yMin, yMax, yStartsAtZero);

  return {
    groups,
    points,
    xDomain,
    yDomain,
    xTicks: niceTicks(xDomain[0], xDomain[1]),
    yTicks: niceTicks(yDomain[0], yDomain[1]),
    zRange,
    skipped,
    warnings,
  };
}
